// Hoja de sesión del estudio (Fase 9.2): panel lateral filtrado por la sala que
// se está viendo. Es la misma información de la vista simple, en texto.
import React from 'react';
import { fmtTime, timeAgo } from '../chat/format';
import { ORIGIN } from '../chat/status';
import { PRESENCE, SESSION_STATE, describeEvent, phoneOf, severityIcon } from '../supervision/labels';
import { agentLights } from '../../hooks/useSupervision';

export default function RoomSheet({ view, data, theme, live, now, feed, onSelectStation }) {
  const names = new Map(data.ai_agents.map(a => [a.id, a.name]));
  const agentName = id => names.get(id) || id;
  const c = theme.colors;

  if (view === 'overview') {
    return (
      <Sheet title="Alertas de las últimas 24 horas" c={c}>
        {data.alerts.length === 0 && <Empty>Sin alertas. Todo en orden.</Empty>}
        {data.alerts.slice(0, 12).map(a => (
          <Row key={a.id} c={c} warn={a.severity}>
            <span>{severityIcon(a.severity)}</span>
            <span style={{ flex: 1 }}>{describeEvent(a, agentName)}</span>
            <span style={st.time}>{fmtTime(a.created_at)}</span>
          </Row>
        ))}
        <p style={st.hint}>Entra a una sala o haz clic en un puesto para ver sus tareas.</p>
      </Sheet>
    );
  }

  if (view === 'control' || view === 'booths') {
    const agents = data.ai_agents.filter(a => (view === 'control' ? a.room === 'control' : a.room !== 'control'));
    const ids = new Set(agents.map(a => a.id));
    const events = feed.filter(e => ids.has(e.actor_id)).slice(0, 15);
    return (
      <Sheet title={theme.rooms[view].label} hint={theme.rooms[view].hint} c={c}>
        {agents.map(a => {
          const l = agentLights(live, a.id, now);
          return (
            <Row key={a.id} c={c} warn={l.clip ? 'error' : null} onClick={() => onSelectStation({ kind: 'ai', id: a.id, room: view })}
              label={`${a.name}: ver sus tareas`}>
              <span style={{ flex: 1 }}>
                <strong>{a.name}</strong> <span style={st.muted}>· {a.tasks_30m} tareas en 30 min</span>
                <div style={st.muted}>
                  {l.busy ? `● ${theme.words.onAir}` : 'En espera'} · {a.turns_15m} turnos en 15 min
                  {a.errors_1h ? ` · ⚠ ${a.errors_1h} errores en 1 h` : ''}
                </div>
              </span>
              <span style={st.time}>{a.last_turn_at ? timeAgo(a.last_turn_at) : '—'} ›</span>
            </Row>
          );
        })}
        <h4 style={st.h4}>Actividad reciente</h4>
        {events.length === 0 && <Empty>Sin actividad desde que abriste el estudio.</Empty>}
        {events.map((e, i) => (
          <Row key={`${e.id || 'live'}-${i}`} c={c} warn={e.severity !== 'info' ? e.severity : null}>
            <span style={{ flex: 1 }}>
              {e.type === 'ai_agent.turn_started' ? `${agentName(e.actor_id)} empezó un turno` : describeEvent(e, agentName)}
              {e.coalesced > 1 ? <span style={st.muted}> (×{e.coalesced})</span> : null}
            </span>
            <span style={st.time}>{fmtTime(e.created_at)}</span>
          </Row>
        ))}
      </Sheet>
    );
  }

  if (view === 'lobby') {
    const queued = data.sessions.rows.filter(r => r.state === 'queued');
    return (
      <Sheet title={theme.rooms.lobby.label} hint={theme.rooms.lobby.hint} c={c}>
        {queued.length === 0 && <Empty>Nadie esperando.</Empty>}
        {queued.map(r => (
          <Row key={r.session_id} c={c}>
            <span style={{ flex: 1 }}>
              <strong>{phoneOf(r.session_id)}</strong>
              <div style={st.muted}>{r.origin ? ORIGIN[r.origin] || r.origin : 'Sin origen'}{r.escalation_reason ? ` · ${r.escalation_reason}` : ''}</div>
            </span>
            <span style={st.time}>espera {timeAgo(r.queued_at)}</span>
          </Row>
        ))}
      </Sheet>
    );
  }

  // Sala de asesores: cada asesor con sus casos abiertos
  const open = data.sessions.rows.filter(r => r.state === 'attending' || r.state === 'waiting_client');
  return (
    <Sheet title={theme.rooms.humans.label} hint={theme.rooms.humans.hint} c={c}>
      {data.humans.length === 0 && <Empty>Sin usuarios activos.</Empty>}
      {data.humans.map(h => {
        const p = PRESENCE[h.presence] || PRESENCE.offline;
        const mine = open.filter(r => r.assigned_user_id === h.id);
        return (
          <div key={h.id} style={{ ...st.row, borderColor: 'var(--border)', flexDirection: 'column', alignItems: 'stretch', cursor: 'pointer' }}
            role="button" tabIndex={0} aria-label={`${h.name}: ver sus casos`}
            onClick={() => onSelectStation({ kind: 'human', id: h.id, room: 'humans' })}
            onKeyDown={e => { if (e.key === 'Enter') onSelectStation({ kind: 'human', id: h.id, room: 'humans' }); }}>
            <div style={{ display: 'flex', gap: 8 }}>
              <strong style={{ flex: 1 }}>{h.name}</strong>
              <span style={st.muted}>{p.icon} {p.label}</span>
            </div>
            {mine.length === 0 && <div style={st.muted}>Sin casos abiertos</div>}
            {mine.map(r => (
              <div key={r.session_id} style={{ display: 'flex', gap: 8, fontSize: 12 }}>
                <span style={{ flex: 1 }}>{SESSION_STATE[r.state].icon} {phoneOf(r.session_id)} <span style={st.muted}>· {SESSION_STATE[r.state].label}</span></span>
                <span style={st.time}>{timeAgo(r.last_message_at)}</span>
              </div>
            ))}
          </div>
        );
      })}
    </Sheet>
  );
}

function Sheet({ title, hint, c, children }) {
  return (
    <aside style={{ ...st.sheet, borderColor: c.cardBorder }}>
      <h3 style={st.h3}>{title}</h3>
      {hint && <p style={st.hint}>{hint}</p>}
      <div style={st.list}>{children}</div>
    </aside>
  );
}

function Row({ children, warn, onClick, label }) {
  const border = warn === 'error' ? 'var(--danger-border)' : warn === 'warning' ? 'var(--warning-border)' : 'var(--border)';
  if (!onClick) return <div style={{ ...st.row, borderColor: border }}>{children}</div>;
  return (
    <div style={{ ...st.row, borderColor: border, cursor: 'pointer' }} role="button" tabIndex={0} aria-label={label}
      onClick={onClick} onKeyDown={e => { if (e.key === 'Enter') onClick(); }}>{children}</div>
  );
}

const Empty = ({ children }) => <div style={st.empty}>{children}</div>;

const st = {
  sheet: { background: 'var(--surface)', border: '1px solid', borderRadius: 14, padding: 14, display: 'flex', flexDirection: 'column', gap: 8, minWidth: 0 },
  h3: { fontSize: 14, fontWeight: 800, color: 'var(--text)', margin: 0 },
  h4: { fontSize: 11, fontWeight: 700, color: 'var(--text-2)', textTransform: 'uppercase', letterSpacing: 0.8, margin: '10px 0 2px' },
  hint: { fontSize: 12, color: 'var(--text-3)', margin: 0 },
  list: { display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '34vh', overflowY: 'auto' },
  row: { display: 'flex', gap: 8, alignItems: 'baseline', border: '1px solid', borderRadius: 8, padding: '7px 10px', fontSize: 12.5, color: 'var(--text)', background: 'var(--surface-2)' },
  muted: { color: 'var(--text-3)', fontSize: 11.5 },
  time: { color: 'var(--text-3)', fontSize: 11, whiteSpace: 'nowrap' },
  empty: { color: 'var(--text-3)', fontSize: 12.5, padding: '6px 0' },
};
